import { useState } from "react";
import { Link } from "react-router-dom";
import { useAccountTemplateAssignments, useListingTemplateOverride } from "@/hooks/useTemplateAssignments";
import {
  templateById,
  templatesForProvider,
  templateChannelLabel,
} from "@/lib/listing-templates";
import TemplateThumbnail from "./TemplateThumbnail";

/**
 * Per-listing template override, shown inside the listing workflow.
 *
 * The normal path is "Use account default". The merchant only sees a change
 * when they deliberately pick a different template for this one listing, and
 * can always reset back to the account default. Only templates compatible with
 * this listing's channel are offered (and the backend enforces the same rule).
 */
export default function ListingTemplateOverride({
  orgId,
  listingId,
  connectorId,
  provider,
}: {
  orgId: string;
  listingId: string;
  connectorId: string | null;
  provider: string | null;
}) {
  const { assignments } = useAccountTemplateAssignments(orgId);
  const { override, set } = useListingTemplateOverride(orgId, listingId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const accountDefaultId = connectorId ? assignments?.[connectorId] ?? null : null;
  const accountDefault = templateById(accountDefaultId);
  const available = templatesForProvider(provider);
  const effectiveId = override ?? accountDefaultId;
  const effective = templateById(effectiveId);

  const commit = async (value: string | null) => {
    setBusy(true);
    setError("");
    try {
      await set(value);
    } catch (err) {
      setError((err as Error).message || "Could not save the template override.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="text-sm font-semibold text-slate-800">Listing template</h3>
        <Link
          to="/listing-templates"
          className="text-xs font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap"
        >
          Manage templates
        </Link>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed">
        How this listing will look on its store. New and updated listings use the account default unless you override it.
      </p>

      {/* Live preview of the effective template */}
      <div className="mt-3 flex items-start gap-3 rounded-lg border border-slate-100 p-3">
        <div className="w-24 h-16 rounded-md border border-slate-200 overflow-hidden shrink-0 bg-slate-50">
          <TemplateThumbnail templateId={effectiveId ?? "clean-commerce"} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-800 truncate">{effective?.name ?? "Clean Commerce"}</p>
          <p className="text-xs text-slate-400">
            {effective ? templateChannelLabel(effective.channels) : ""}
          </p>
          <div className="mt-1.5 flex items-center gap-2">
            <Link
              to={`/listing-templates/preview/${effectiveId ?? "clean-commerce"}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800"
            >
              <i className="ri-eye-line" /> Preview
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-3">
        <label className="block text-xs font-medium text-slate-500 mb-1">Template for this listing</label>
        <select
          value={override ?? ""}
          disabled={busy}
          onChange={(e) => commit(e.target.value || null)}
          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60"
        >
          <option value="">
            Use account default{accountDefault ? ` — ${accountDefault.name}` : ""}
          </option>
          {available.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      {override && (
        <button
          type="button"
          disabled={busy}
          onClick={() => commit(null)}
          className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 disabled:opacity-60"
        >
          <i className="ri-arrow-go-back-line" /> Reset to account default
        </button>
      )}

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      <div className="mt-3 flex items-center gap-2 text-xs">
        {override ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 font-medium">
            <i className="ri-settings-3-line" /> Overridden for this listing — {effective?.name}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">
            <i className="ri-check-line" /> Using account default — {accountDefault?.name ?? "Clean Commerce"}
          </span>
        )}
      </div>

      <p className="mt-3 text-xs text-slate-400 leading-relaxed">
        Changing this only affects this listing when it is next published or updated. Already-published listings are not
        changed automatically.
      </p>
    </div>
  );
}